import React, { useEffect, useRef, useState } from 'react';
import { IAttachSign, IAttachViewSignModel, ISignVerificationResult } from '@models/attaches/IAttachViewSignModel';
import { AttachActionsService } from '@services/actions/AttachActionsService';
import { CadespluginService } from '@services/CadespluginService';
import Modal from '@srm/ui/modal';
import clsx from 'clsx';
import Button from '@srm/ui/button';
import ModalLoadingOverlay from '@srm/ui/modal-loading-overlay';
import Hint from '@srm/ui/hint';
import { safeFormatDate } from '@utils/date';
import { MdOutlineVerifiedUser } from 'react-icons/md';
import SelectableList from '@srm/ui/selectable-list';
import { stringifyError } from '@srm/core/errors';
import { IViewAttachSignProps } from './IViewAttachSignProps';

export const ViewAttachSignModal: React.FC<IViewAttachSignProps> = (props: IViewAttachSignProps) => {
    const [isBusy, setIsBusy] = useState<boolean>(true);
    const [signsModel, setSignsModel] = useState<IAttachViewSignModel>();

    const [verificationResult, setVerificationResult] = useState<ISignVerificationResult>();
    const [signUnderVerification, setSignUnderVerification] = useState<IAttachSign>();
    const [errorText, setErrorText] = useState<string>();
    // Окно только показывает подписи: закрыть его можно в любой момент, а ответы загрузки и
    // проверки, пришедшие после закрытия, не применяются.
    const closedRef = useRef(false);
    useEffect(() => {
        closedRef.current = false;
        return () => {
            closedRef.current = true;
        };
    }, []);

    const service = new AttachActionsService(props.docId, '-1');

    const signItems = (signsModel?.signs ?? []).map((sign) => ({
        key: sign.key,
        title: sign.signAuthor,
        subtitle: sign.date ? safeFormatDate(sign.date, 'DD.MM.YYYY HH:mm') : undefined,
        icon: <MdOutlineVerifiedUser size={18} />,
    }));

    const verifyCheck = async (signId: string) => {
        setIsBusy(true);
        setErrorText(undefined);
        try {
            const serv = new CadespluginService();
            const fileWithContent = (await service.getAttachSignsWithContentBase64(props.docId, props.attachId, signId))
                .data;
            // Окно закрыли — плагин подписи не трогаем: его запрос доступа всплыл бы уже без окна.
            if (closedRef.current) return;
            const verifyResult = await serv.verifyData(
                fileWithContent.attachContent,
                fileWithContent.signContent,
                fileWithContent.algorithm,
            );

            if (!closedRef.current) setVerificationResult(verifyResult);
        } catch (error) {
            if (!closedRef.current) setErrorText(stringifyError(error));
        } finally {
            if (!closedRef.current) setIsBusy(false);
        }

        // if(verifyResult?.suceeded){
        //     alert('Подпись проверена успешно');

        // }else{
        //     alert('Ошибка проверки подписи: ' + errorString);
        // }
    };

    useEffect(() => {
        let ignore = false;
        setIsBusy(true);

        service
            .getAttachSigns(props.attachId)
            .then((res) => {
                if (!ignore) setSignsModel(res.data);
            })
            .catch((error: unknown) => {
                if (!ignore) setErrorText(stringifyError(error));
            })
            .finally(() => {
                if (!ignore) setIsBusy(false);
            });
        return () => {
            ignore = true;
        };
    }, []);

    return (
        <Modal
            className={clsx('modal-dlg-container')}
            size={props.modalSize}
            onClose={() => {
                if (props.completeHandler) props.completeHandler(false);
            }}
            header={`Информация о подписях файла "${signsModel?.fileName}"`}
            footer={
                <div className="modal-dlg-buttons">
                    <Button
                        size="sm"
                        aria-label={props.cancelButtonText}
                        onClick={() => {
                            if (props.completeHandler) props.completeHandler(false);
                        }}
                    >
                        {props.cancelButtonText}
                    </Button>
                </div>
            }
        >
            <ModalLoadingOverlay visible={isBusy}>
                <div>
                    {errorText && <Hint role="alert" title={`Ошибка: ${errorText}`} tone="danger" maxWidth="100%" />}
                    {verificationResult && (
                        <Hint
                            role="alert"
                            tone={verificationResult.suceeded ? 'success' : 'danger'}
                            title="Проверка подписи"
                            onClose={() => {
                                setVerificationResult(undefined);
                            }}
                            maxWidth=""
                        >
                            <div>Владелец сертификата: {verificationResult.certOwner}</div>
                            <div>Серийный номер: {verificationResult.serialNumber}</div>
                            <div>
                                Период действия: {safeFormatDate(verificationResult.validFrom, 'DD.MM.YYYY HH:mm')} -{' '}
                                {safeFormatDate(verificationResult.validTo, 'DD.MM.YYYY HH:mm')}
                            </div>
                            <div>
                                Результат:{' '}
                                {verificationResult.suceeded
                                    ? 'Подпись успешно проверена'
                                    : 'Ошибка: ' + verificationResult.errorText}
                            </div>
                            <div>Документ подписал: {signUnderVerification?.signAuthor}</div>
                            <div>
                                Дата подписи:{' '}
                                {signUnderVerification?.date &&
                                    safeFormatDate(signUnderVerification.date, 'DD.MM.YYYY HH:mm')}
                            </div>
                        </Hint>
                    )}

                    <SelectableList
                        mode="none"
                        items={signItems}
                        selected={[]}
                        onChange={() => {}}
                        emptyText="Нет подписей"
                        ariaLabel="Подписи вложения"
                        renderActions={(item) => (
                            <>
                                <Button
                                    variant="text"
                                    size="xs"
                                    aria-label={`Проверить подпись: ${item.title}`}
                                    onClick={() => {
                                        const sign = signsModel?.signs?.find((s) => s.key === item.key);
                                        if (!sign) return;
                                        setSignUnderVerification(sign);
                                        verifyCheck(sign.key);
                                    }}
                                >
                                    Проверить
                                </Button>
                                <Button
                                    variant="text"
                                    size="xs"
                                    aria-label={`Скачать подпись: ${item.title}`}
                                    onClick={() => service.downloadAttachSigns(props.attachId, item.key)}
                                >
                                    Скачать
                                </Button>
                            </>
                        )}
                    />
                </div>
            </ModalLoadingOverlay>
        </Modal>
    );
};
